import { sameRef } from "../configuration";
import type { ChargePointSimulator, EvseState } from "../simulator";
import { certificateHashData, fakeCsr, LOG_STATUSES, sameHash, startFirmwareUpdate, startPublishFirmware, startUpload } from "./shared";

type Handlers = Record<string, (params: any) => Promise<any>>;

const REPORT_PAGE_SIZE = 20;

/** Answers every CSMS-initiated OCPP 2.0.1 message, and the OCPP 2.1 ones on top. */
export const createV2xHandlers = (sim: ChargePointSimulator): Handlers => {
  const evse = (id: number | undefined): EvseState | undefined => (id === undefined ? undefined : sim.evse(id));
  const findTx = (transactionId: string) => sim.evses.find(e => e.transaction?.id === transactionId);
  const firstFree = () => sim.evses.find(e => !e.transaction && e.operative && e.status !== "Faulted" && !e.reservation);

  /** Send `items` as NotifyReport-style pages (seqNo / tbc). */
  const sendPaged = (action: string, items: any[], key: string, base: Record<string, any>) => {
    const pages = Math.max(1, Math.ceil(items.length / REPORT_PAGE_SIZE));
    sim.later(async () => {
      for (let seqNo = 0; seqNo < pages; seqNo++) {
        const page = items.slice(seqNo * REPORT_PAGE_SIZE, (seqNo + 1) * REPORT_PAGE_SIZE);
        await sim.call(action, {
          ...base,
          generatedAt: sim.timestamp(),
          seqNo,
          tbc: seqNo < pages - 1,
          ...(page.length > 0 && { [key]: page }),
        });
      }
    });
  };

  const trigger = async (requestedMessage: string, target?: { id: number; connectorId?: number }) => {
    const targetEvse = evse(target?.id);
    switch (requestedMessage) {
      case "BootNotification":
        return sim.boot();
      case "Heartbeat":
        return sim.heartbeat();
      case "StatusNotification":
        return targetEvse ? sim.sendStatus(targetEvse) : sim.sendAllStatuses();
      case "MeterValues":
        for (const e of targetEvse ? [targetEvse] : sim.evses) await sim.sendMeterValues(e.id, "Trigger");
        return;
      case "TransactionEvent":
        for (const e of (targetEvse ? [targetEvse] : sim.evses).filter(x => x.transaction)) await sim.sendMeterValues(e.id, "Trigger");
        return;
      case "LogStatusNotification":
        return sim.call("LogStatusNotification", { status: sim.uploading ? "Uploading" : "Idle" });
      case "FirmwareStatusNotification":
        return sim.call("FirmwareStatusNotification", { status: sim.records.firmwareStatus ?? "Idle" });
      case "PublishFirmwareStatusNotification":
        return sim.call("PublishFirmwareStatusNotification", { status: sim.publishedFirmware ? "Published" : "Idle" });
      case "SignChargingStationCertificate":
        return sim.call("SignCertificate", { csr: fakeCsr(sim.settings.chargePointId), certificateType: "ChargingStationCertificate" });
      case "SignV2GCertificate":
      case "SignCombinedCertificate":
        return sim.call("SignCertificate", { csr: fakeCsr(sim.settings.chargePointId), certificateType: "V2GCertificate" });
      default:
        return undefined;
    }
  };
  const TRIGGERABLE = new Set([
    "BootNotification",
    "Heartbeat",
    "StatusNotification",
    "MeterValues",
    "TransactionEvent",
    "LogStatusNotification",
    "FirmwareStatusNotification",
    "PublishFirmwareStatusNotification",
    "SignChargingStationCertificate",
    "SignV2GCertificate",
    "SignCombinedCertificate",
  ]);

  const setAvailability = (target: EvseState, operative: boolean) => {
    if (target.transaction && !operative) {
      target.pendingInoperative = true;
      return "Scheduled";
    }
    target.operative = operative;
    target.pendingInoperative = false;
    target.status = operative ? (target.reservation ? "Reserved" : "Available") : "Unavailable";
    sim.emitEvse(target);
    sim.later(() => sim.sendStatus(target).then(() => undefined));
    return "Accepted";
  };

  const handlers: Handlers = {
    // ---------------------------------------------------------------- Provisioning / device model
    async GetVariables({ getVariableData }) {
      return {
        getVariableResult: getVariableData.map((item: any) => {
          const result = sim.config.getVariable(item.component, item.variable, item.attributeType);
          return {
            attributeStatus: result.status,
            ...(item.attributeType && { attributeType: item.attributeType }),
            ...(result.value !== undefined && { attributeValue: result.value.slice(0, 2500) }),
            component: item.component,
            variable: item.variable,
          };
        }),
      };
    },

    async SetVariables({ setVariableData }) {
      return {
        setVariableResult: setVariableData.map((item: any) => ({
          ...(item.attributeType && { attributeType: item.attributeType }),
          attributeStatus: sim.config.setVariable(item.component, item.variable, item.attributeValue, item.attributeType),
          component: item.component,
          variable: item.variable,
        })),
      };
    },

    async GetBaseReport({ requestId, reportBase }) {
      const data =
        reportBase === "SummaryInventory"
          ? sim.config.report(entry => entry.component.name === "ChargingStation" || entry.component.name === "EVSE")
          : sim.config.report(reportBase === "ConfigurationInventory" ? entry => entry.mutability !== "ReadOnly" : undefined);
      if (data.length === 0) return { status: "EmptyResultSet" };
      sendPaged("NotifyReport", data, "reportData", { requestId });
      return { status: "Accepted" };
    },

    async GetReport({ requestId, componentVariable, componentCriteria }) {
      const data = sim.config.report(entry => {
        if (componentVariable?.length) {
          const matches = componentVariable.some(
            (cv: any) =>
              cv.component.name === entry.component.name &&
              (cv.component.instance === undefined || cv.component.instance === entry.component.instance) &&
              (!cv.variable || sameRef(cv.component, entry.component, cv.variable, entry.variable) || cv.variable.name === entry.variable.name)
          );
          if (!matches) return false;
        }
        if (componentCriteria?.length) {
          // Every simulated component is active, available and enabled; none has a problem.
          return componentCriteria.some((c: string) => c !== "Problem");
        }
        return true;
      });
      if (data.length === 0) return { status: "EmptyResultSet" };
      sendPaged("NotifyReport", data, "reportData", { requestId });
      return { status: "Accepted" };
    },

    async SetNetworkProfile({ configurationSlot, connectionData }) {
      sim.networkProfiles.set(configurationSlot, connectionData);
      return { status: "Accepted" };
    },

    async Reset({ type, evseId }) {
      if (evseId !== undefined && !evse(evseId)) return { status: "Rejected" };
      const busy = sim.evses.some(e => e.transaction);
      if (type === "OnIdle" && busy) {
        sim.pendingReset = "OnIdle";
        return { status: "Scheduled" };
      }
      sim.later(async () => {
        for (const e of sim.evses) if (e.transaction) await sim.stopTransaction(e.id, "ImmediateReset");
        sim.reboot("RemoteReset");
      });
      return { status: "Accepted" };
    },

    // ---------------------------------------------------------------- Availability
    async ChangeAvailability({ operationalStatus, evse: target }) {
      const operative = operationalStatus === "Operative";
      const targets = target ? [evse(target.id)].filter(Boolean) as EvseState[] : sim.evses;
      if (targets.length === 0) return { status: "Rejected" };
      const results = targets.map(e => setAvailability(e, operative));
      return { status: results.includes("Scheduled") ? "Scheduled" : "Accepted" };
    },

    async UnlockConnector({ evseId, connectorId }) {
      const target = evse(evseId);
      if (!target || connectorId !== 1) return { status: "UnknownConnector" };
      if (target.transaction?.authorized) return { status: "OngoingAuthorizedTransaction" };
      return { status: target.unlockResult };
    },

    async TriggerMessage({ requestedMessage, evse: target }) {
      if (!TRIGGERABLE.has(requestedMessage)) return { status: "NotImplemented" };
      if (target && !evse(target.id)) return { status: "Rejected" };
      if (requestedMessage === "TransactionEvent" && !(target ? evse(target.id)?.transaction : sim.evses.some(e => e.transaction))) {
        return { status: "Rejected" };
      }
      sim.later(() => trigger(requestedMessage, target).then(() => undefined));
      return { status: "Accepted" };
    },

    // ---------------------------------------------------------------- Transactions
    async RequestStartTransaction({ evseId, remoteStartId, idToken, chargingProfile }) {
      const target = evseId !== undefined ? evse(evseId) : firstFree();
      if (!target || target.transaction || !target.operative || target.status === "Faulted") return { status: "Rejected" };
      if (target.reservation && target.reservation.idTag !== idToken.idToken) return { status: "Rejected" };
      if (chargingProfile && chargingProfile.chargingProfilePurpose !== "TxProfile") return { status: "Rejected" };
      sim.later(async () => {
        if (sim.config.getBoolean("AuthorizeRemoteTxRequests")) {
          const status = await sim.authorize(idToken.idToken);
          if (status !== "Accepted") return;
        }
        await sim.startTransaction(target.id, { idTag: idToken.idToken, remoteStartId, chargingProfile });
      });
      return { status: "Accepted" };
    },

    async RequestStopTransaction({ transactionId }) {
      const target = findTx(transactionId);
      if (!target) return { status: "Rejected" };
      sim.later(() => sim.stopTransaction(target.id, "Remote"));
      return { status: "Accepted" };
    },

    async GetTransactionStatus({ transactionId }) {
      return {
        messagesInQueue: false,
        ...(transactionId !== undefined && { ongoingIndicator: Boolean(findTx(transactionId)) }),
      };
    },

    async CostUpdated({ totalCost, transactionId }) {
      const target = findTx(transactionId);
      if (target?.transaction) target.transaction.totalCost = totalCost;
      return {};
    },

    // ---------------------------------------------------------------- Authorization
    async ClearCache() {
      sim.records.authorizationCache = {};
      return { status: "Accepted" };
    },

    async GetLocalListVersion() {
      return { versionNumber: sim.localList.version };
    },

    async SendLocalList({ versionNumber, updateType, localAuthorizationList = [] }) {
      if (!sim.config.getBoolean("LocalAuthListEnabled")) return { status: "Failed" };
      if (updateType === "Differential" && versionNumber <= sim.localList.version) return { status: "VersionMismatch" };
      if (updateType === "Full") sim.localList.entries.clear();
      for (const entry of localAuthorizationList) {
        if (entry.idTokenInfo) sim.localList.entries.set(entry.idToken.idToken, { idTokenInfo: entry.idTokenInfo });
        else sim.localList.entries.delete(entry.idToken.idToken);
      }
      sim.localList.version = versionNumber;
      return { status: "Accepted" };
    },

    // ---------------------------------------------------------------- Reservation
    async ReserveNow({ id, expiryDateTime, idToken, evseId }) {
      const target = evseId !== undefined ? evse(evseId) : firstFree();
      if (!target) return { status: evseId !== undefined ? "Rejected" : "Occupied" };
      if (target.status === "Faulted") return { status: "Faulted" };
      if (!target.operative) return { status: "Unavailable" };
      const reservedByOther = target.reservation && target.reservation.id !== id;
      if (target.transaction || reservedByOther || (target.status !== "Available" && target.status !== "Reserved")) {
        return { status: "Occupied" };
      }
      sim.reserve(target, { id, idTag: idToken.idToken, expiresAt: expiryDateTime });
      sim.later(() => sim.sendStatus(target).then(() => undefined));
      return { status: "Accepted" };
    },

    async CancelReservation({ reservationId }) {
      const target = sim.evses.find(e => e.reservation?.id === reservationId);
      if (!target) return { status: "Rejected" };
      sim.clearReservation(target, true);
      return { status: "Accepted" };
    },

    // ---------------------------------------------------------------- Smart charging
    async SetChargingProfile({ evseId, chargingProfile }) {
      if (chargingProfile.stackLevel > sim.config.getNumber("ChargeProfileMaxStackLevel", 8)) return { status: "Rejected" };
      if (chargingProfile.chargingProfilePurpose === "ChargingStationMaxProfile" && evseId !== 0) return { status: "Rejected" };
      if (chargingProfile.chargingProfilePurpose === "TxProfile") {
        const target = evse(evseId);
        if (!target?.transaction) return { status: "Rejected" };
        if (chargingProfile.transactionId && chargingProfile.transactionId !== target.transaction.id) return { status: "Rejected" };
        sim.profiles.install(evseId, { ...chargingProfile, transactionId: target.transaction.id });
        return { status: "Accepted" };
      }
      if (evseId !== 0 && !evse(evseId)) return { status: "Rejected" };
      sim.profiles.install(evseId, chargingProfile);
      return { status: "Accepted" };
    },

    async ClearChargingProfile({ chargingProfileId, chargingProfileCriteria }) {
      const removed = sim.profiles.clear({
        id: chargingProfileId,
        evseId: chargingProfileCriteria?.evseId,
        purpose: chargingProfileCriteria?.chargingProfilePurpose,
        stackLevel: chargingProfileCriteria?.stackLevel,
      });
      return { status: removed > 0 ? "Accepted" : "Unknown" };
    },

    async GetChargingProfiles({ requestId, evseId, chargingProfile: criteria }) {
      const matches = sim.profiles.find(
        installed =>
          (evseId === undefined || installed.evseId === evseId) &&
          (!criteria?.chargingProfilePurpose || installed.profile.chargingProfilePurpose === criteria.chargingProfilePurpose) &&
          (criteria?.stackLevel === undefined || installed.profile.stackLevel === criteria.stackLevel) &&
          (!criteria?.chargingProfileId?.length || criteria.chargingProfileId.includes(installed.profile.id))
      );
      if (matches.length === 0) return { status: "NoProfiles" };
      const byEvse = new Map<number, any[]>();
      matches.forEach(m => byEvse.set(m.evseId, [...(byEvse.get(m.evseId) ?? []), m.profile]));
      sim.later(async () => {
        const entries = [...byEvse.entries()];
        for (let i = 0; i < entries.length; i++) {
          const [id, profiles] = entries[i];
          await sim.call("ReportChargingProfiles", {
            requestId,
            chargingLimitSource: "CSO",
            evseId: id,
            tbc: i < entries.length - 1,
            chargingProfile: profiles,
          });
        }
      });
      return { status: "Accepted" };
    },

    async GetCompositeSchedule({ duration, chargingRateUnit, evseId }) {
      if (evseId !== 0 && !evse(evseId)) return { status: "Rejected" };
      const schedule = sim.profiles.composite(evseId, duration, chargingRateUnit, evse(evseId)?.transaction?.id);
      if (!schedule) return { status: "Rejected" };
      return {
        status: "Accepted",
        schedule: {
          evseId,
          duration: schedule.duration,
          scheduleStart: sim.timestamp(),
          chargingRateUnit: schedule.chargingRateUnit,
          chargingSchedulePeriod: schedule.chargingSchedulePeriod,
        },
      };
    },

    // ---------------------------------------------------------------- Firmware & diagnostics
    async UpdateFirmware({ requestId, retries, retryInterval, firmware }) {
      if (firmware.signingCertificate !== undefined && !firmware.signingCertificate.includes("BEGIN CERTIFICATE")) {
        return { status: "InvalidCertificate" };
      }
      const { cancelledPrevious } = startFirmwareUpdate(sim, {
        action: "UpdateFirmware",
        location: firmware.location,
        retrieveAt: firmware.retrieveDateTime,
        installAt: firmware.installDateTime,
        retries,
        retryInterval,
        signed: firmware.signature !== undefined,
        extendedStatuses: true,
        send: status => sim.call("FirmwareStatusNotification", { status, requestId }),
      });
      return { status: cancelledPrevious ? "AcceptedCanceled" : "Accepted" };
    },

    async PublishFirmware({ location, checksum, requestId, retries, retryInterval }) {
      startPublishFirmware(sim, {
        location,
        checksum,
        retries,
        retryInterval,
        send: (status, extra) => sim.call("PublishFirmwareStatusNotification", { status, requestId, ...extra }),
      });
      return { status: "Accepted" };
    },

    async UnpublishFirmware({ checksum }) {
      if (!sim.publishedFirmware || sim.publishedFirmware.checksum !== checksum) return { status: "NoFirmware" };
      sim.publishedFirmware = null;
      return { status: "Unpublished" };
    },

    async GetLog({ logType, requestId, retries, retryInterval, log }) {
      const { fileName, cancelledPrevious } = startUpload(sim, {
        action: "GetLog",
        location: log.remoteLocation,
        retries,
        retryInterval,
        from: log.oldestTimestamp,
        to: log.latestTimestamp,
        logType,
        statuses: LOG_STATUSES,
        send: status => sim.call("LogStatusNotification", { status, requestId }),
      });
      return { status: cancelledPrevious ? "AcceptedCanceled" : "Accepted", filename: fileName };
    },

    async CustomerInformation({ requestId, report, clear }) {
      if (report) {
        sim.later(() =>
          sim
            .call("NotifyCustomerInformation", {
              data: clear ? "Customer data cleared" : "No customer data stored by this simulator",
              tbc: false,
              seqNo: 0,
              generatedAt: sim.timestamp(),
              requestId,
            })
            .then(() => undefined)
        );
      }
      return { status: "Accepted" };
    },

    async DataTransfer({ vendorId, messageId, data }) {
      sim.records.DataTransfer = { vendorId, messageId, data };
      return { status: "Accepted" };
    },

    // ---------------------------------------------------------------- Security / certificates
    async CertificateSigned({ certificateChain, certificateType }) {
      if (!certificateChain.includes("BEGIN CERTIFICATE")) return { status: "Rejected" };
      sim.certificates.push({
        certificateType: certificateType ?? "ChargingStationCertificate",
        certificate: certificateChain,
        hashData: certificateHashData(certificateChain),
      });
      return { status: "Accepted" };
    },

    async InstallCertificate({ certificateType, certificate }) {
      if (!certificate.includes("BEGIN CERTIFICATE")) return { status: "Rejected" };
      sim.certificates.push({ certificateType, certificate, hashData: certificateHashData(certificate) });
      return { status: "Accepted" };
    },

    async DeleteCertificate({ certificateHashData: hash }) {
      const index = sim.certificates.findIndex(c => sameHash(c.hashData, hash));
      if (index < 0) return { status: "NotFound" };
      sim.certificates.splice(index, 1);
      return { status: "Accepted" };
    },

    async GetInstalledCertificateIds({ certificateType }) {
      const found = sim.certificates.filter(c => !certificateType?.length || certificateType.includes(c.certificateType));
      if (found.length === 0) return { status: "NotFound" };
      return {
        status: "Accepted",
        certificateHashDataChain: found.map(c => ({ certificateType: c.certificateType, certificateHashData: c.hashData })),
      };
    },

    // ---------------------------------------------------------------- Display messages
    async SetDisplayMessage({ message }) {
      if (message.transactionId && !findTx(message.transactionId)) return { status: "UnknownTransaction" };
      sim.displayMessages.set(message.id, message);
      return { status: "Accepted" };
    },

    async GetDisplayMessages({ id, requestId, priority, state }) {
      const messages = [...sim.displayMessages.values()].filter(
        m => (!id?.length || id.includes(m.id)) && (!priority || m.priority === priority) && (!state || m.state === state)
      );
      if (messages.length === 0) return { status: "Unknown" };
      sim.later(() => sim.call("NotifyDisplayMessages", { requestId, tbc: false, messageInfo: messages }).then(() => undefined));
      return { status: "Accepted" };
    },

    async ClearDisplayMessage({ id }) {
      return { status: sim.displayMessages.delete(id) ? "Accepted" : "Unknown" };
    },

    // ---------------------------------------------------------------- Monitoring
    async SetMonitoringBase({ monitoringBase }) {
      sim.records.monitoringBase = monitoringBase;
      if (monitoringBase === "HardWiredOnly") sim.monitors.clear();
      return { status: "Accepted" };
    },

    async SetMonitoringLevel({ severity }) {
      sim.records.monitoringLevel = severity;
      return { status: "Accepted" };
    },

    async SetVariableMonitoring({ setMonitoringData }) {
      return {
        setMonitoringResult: setMonitoringData.map((item: any) => {
          const known = sim.config.findByRef(item.component, item.variable);
          const base = { type: item.type, severity: item.severity, component: item.component, variable: item.variable };
          if (!known) return { ...base, status: sim.config.all().some(e => e.component.name === item.component.name) ? "UnknownVariable" : "UnknownComponent" };
          const id = item.id ?? Math.max(0, ...sim.monitors.keys()) + 1;
          sim.monitors.set(id, { ...item, id });
          return { ...base, id, status: "Accepted" };
        }),
      };
    },

    async ClearVariableMonitoring({ id }) {
      return {
        clearMonitoringResult: id.map((monitorId: number) => ({
          id: monitorId,
          status: sim.monitors.delete(monitorId) ? "Accepted" : "NotFound",
        })),
      };
    },

    async GetMonitoringReport({ requestId }) {
      const monitors = [...sim.monitors.values()];
      if (monitors.length === 0) return { status: "EmptyResultSet" };
      const report = monitors.map(m => ({
        component: m.component,
        variable: m.variable,
        variableMonitoring: [{ id: m.id, transaction: Boolean(m.transaction), value: m.value, type: m.type, severity: m.severity }],
      }));
      sendPaged("NotifyMonitoringReport", report, "monitor", { requestId });
      return { status: "Accepted" };
    },
  };

  if (sim.protocol === "ocpp2.1") {
    // OCPP 2.1 additions with charge-point side effects; the remaining 2.1 messages (DER control,
    // tariffs, periodic event streams, battery swap, ...) get the default schema-valid answer and
    // are recorded on the simulator.
    handlers.ChangeTransactionTariff = async ({ transactionId, tariff }) => {
      const target = findTx(transactionId);
      if (!target) return { status: "TxNotFound" };
      sim.records.tariffs = { ...(sim.records.tariffs ?? {}), [transactionId]: tariff };
      return { status: "Accepted" };
    };
    handlers.UsePriorityCharging = async ({ transactionId, activate }) => {
      if (!findTx(transactionId)) return { status: "NoProfile" };
      sim.records.priorityCharging = { ...(sim.records.priorityCharging ?? {}), [transactionId]: activate };
      return { status: "Accepted" };
    };
  }

  return handlers;
};
